import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import api from '../../services/api'

type AuditResource = {
  resource_type?: 'mock' | 'brand' | 'template'
  resource_id?: string
}

function routeContext(pathname: string): AuditResource & { route_name: string } {
  const useCaseMatch = pathname.match(/^\/use-cases\/([^/]+)$/)
  if (useCaseMatch) {
    return { resource_type: 'mock', resource_id: useCaseMatch[1], route_name: 'Use case workspace' }
  }
  if (pathname === '/use-cases') return { route_name: 'Use cases' }
  if (pathname === '/brands') return { route_name: 'Brands' }
  if (pathname === '/templates') return { route_name: 'Templates' }
  if (pathname === '/figma-templates') return { route_name: 'Frame templates' }
  if (pathname.startsWith('/tenant-admin/users')) return { route_name: 'User management' }
  if (pathname.startsWith('/tenant-admin/audit-log')) return { route_name: 'Audit log' }
  if (pathname.startsWith('/tenant-admin')) return { route_name: 'Tenant administration' }
  if (pathname.startsWith('/sales-engagement')) return { route_name: 'Sales engagement' }
  if (pathname === '/published-assets') return { route_name: 'Published assets' }
  if (pathname === '/collaboration') return { route_name: 'Collaboration' }
  if (pathname === '/settings') return { route_name: 'Settings' }
  return { route_name: pathname === '/' || pathname === '/dashboard' ? 'Dashboard' : 'Application page' }
}

function controlLabel(control: HTMLElement): string {
  const explicit = control.dataset.auditLabel
    || control.getAttribute('aria-label')
    || control.getAttribute('title')
  const text = explicit || control.textContent || ''
  return text.replace(/\s+/g, ' ').trim().slice(0, 160)
}

async function postEvent(payload: Record<string, unknown>) {
  try {
    await api.post('/profile/enhanced-audit-events', payload)
  } catch {
    // Observability must never interrupt the user's work.
  }
}

/**
 * Selected-user activity collector.
 *
 * It records route changes and meaningful button/link use, never input values,
 * conversation text, passwords, or raw form contents.
 */
export default function EnhancedAuditTracker() {
  const location = useLocation()
  const [enabled, setEnabled] = useState(false)
  const lastInteraction = useRef<{ key: string; at: number } | null>(null)

  useEffect(() => {
    let active = true
    const refresh = () => {
      api.get('/profile/enhanced-audit-status')
        .then(({ data }) => {
          if (active) setEnabled(Boolean(data?.enabled))
        })
        .catch(() => {})
    }
    refresh()
    const interval = window.setInterval(refresh, 30_000)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') refresh()
    }
    const handleChanged = (event: Event) => {
      const detail = (event as CustomEvent<{ enabled?: boolean }>).detail
      if (typeof detail?.enabled === 'boolean') setEnabled(detail.enabled)
      else refresh()
    }
    document.addEventListener('visibilitychange', handleVisibility)
    window.addEventListener('enhanced-audit:changed', handleChanged)
    return () => {
      active = false
      window.clearInterval(interval)
      document.removeEventListener('visibilitychange', handleVisibility)
      window.removeEventListener('enhanced-audit:changed', handleChanged)
    }
  }, [])

  useEffect(() => {
    if (!enabled) return
    const route = routeContext(location.pathname)
    void postEvent({
      event_type: 'navigation',
      label: route.route_name,
      path: location.pathname,
      resource_type: route.resource_type,
      resource_id: route.resource_id,
      context: { route_name: route.route_name },
    })
  }, [enabled, location.pathname])

  useEffect(() => {
    if (!enabled) return
    const handleClick = (event: MouseEvent) => {
      const target = event.target instanceof Element
        ? event.target.closest<HTMLElement>('button, a[href], [role="button"]')
        : null
      if (!target || target.dataset.auditIgnore === 'true' || target.hasAttribute('disabled')) return

      const label = controlLabel(target)
      if (!label) return
      const key = `${location.pathname}:${label}`
      const now = Date.now()
      if (lastInteraction.current?.key === key && now - lastInteraction.current.at < 1000) return
      lastInteraction.current = { key, at: now }

      const route = routeContext(location.pathname)
      const href = target instanceof HTMLAnchorElement
        ? new URL(target.href, window.location.origin).pathname
        : undefined
      void postEvent({
        event_type: 'interaction',
        label,
        path: location.pathname,
        resource_type: route.resource_type,
        resource_id: route.resource_id,
        context: {
          route_name: route.route_name,
          control_kind: target.tagName.toLowerCase(),
          ...(href ? { destination: href } : {}),
        },
      })
    }
    document.addEventListener('click', handleClick, true)
    return () => document.removeEventListener('click', handleClick, true)
  }, [enabled, location.pathname])

  return null
}
